import { useNotifications } from "@via/hooks/useNotifications";
import { axios } from "@via/api/axios.tsx";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  DatasetStagingStatus,
  ListDatasetStagingResponse,
  StartDatasetStagingRequestBody,
  StartDatasetStagingResponse,
} from "common";

const stagingKey = (datasetId: string): unknown[] => [
  "/api/v1/vmeta/dataset",
  datasetId,
  "staging",
];

export const useDatasetStagingList = (
  datasetId: string | undefined,
  enabled: boolean
): UseQueryResult<ListDatasetStagingResponse, Error> =>
  useQuery({
    queryKey: stagingKey(datasetId ?? ""),
    enabled: Boolean(datasetId) && enabled,
    queryFn: async () => {
      const res = await axios.get(`/api/v1/vmeta/dataset/${datasetId}/staging`);
      return res.data as ListDatasetStagingResponse;
    },
    // A copy of a large dataset runs for a long time, so this polls rather than leaving the card
    // stale until the user reloads.
    refetchInterval: query =>
      (
        query.state.data as ListDatasetStagingResponse | undefined
      )?.generations.some(
        g =>
          g.status === DatasetStagingStatus.Pending ||
          g.status === DatasetStagingStatus.InProgress
      )
        ? 5000
        : false,
  });

export const useStartDatasetStaging = (datasetId: string | undefined) => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: StartDatasetStagingRequestBody) => {
      const res = await axios.post(
        `/api/v1/vmeta/dataset/${datasetId}/staging`,
        payload
      );
      return res.data as StartDatasetStagingResponse;
    },
    onSuccess: async result => {
      // Invalidating here rather than relying on the poll, because the first render after starting
      // a copy has nothing to poll for yet.
      await queryClient.invalidateQueries({
        queryKey: stagingKey(datasetId ?? ""),
      });
      setNotification({
        id: "dataset-staging-started",
        message: result.joined
          ? "Joined the copy already running"
          : "Copying this dataset to the cloud",
        details: result.joined
          ? "Somebody is already copying this dataset to that destination, so this joined it."
          : `${result.objectCount} files are on their way. You can leave this page.`,
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "dataset-staging-failed",
        message: "We could not start the copy",
        details:
          error?.response?.data?.message ??
          "Please try again, or pick a different destination.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCancelDatasetStaging = (datasetId: string | undefined) => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (generationKey: string) => {
      const res = await axios.delete(
        `/api/v1/vmeta/dataset/${datasetId}/staging/${generationKey}`
      );
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stagingKey(datasetId ?? ""),
      });
      setNotification({
        id: "dataset-staging-cancelled",
        message: "Copy cancelled",
        details: "Files already copied are still in your bucket.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "dataset-staging-cancel-failed",
        message: "We could not cancel the copy",
        details: error?.response?.data?.message ?? "Please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
